import { Ref } from "react";
import styled from "styles/styled_components";
import { IThemeInterface } from "styles/theme";

export enum ButtonColor {
  BLUE = "BLUE",
  GRAY = "GRAY",
  GREEN = "GREEN",
  RED = "RED",
  WHITE = "WHITE",
}

export interface IBaseButtonProps {
  borderless?: boolean;
  buttonColor: string;
  ref?: Ref<HTMLButtonElement>;
}

interface IButtonColors {
  color: string;
  backgroundColor?: string;
  background?: string;
  borderColor?: string;
}

function getButtonColors(
  props: { theme: IThemeInterface } & IBaseButtonProps
): IButtonColors {
  // Default font color is white
  const color = props.theme.colors.brandWhite;
  switch (props.buttonColor) {
    case ButtonColor.GRAY:
      return {
        color: props.theme.colors.lspdGray800,
        background: props.theme.colors.lspdSecondaryGradient,
        borderColor: props.theme.colors.lspdSecondaryBorder,
      };
    case ButtonColor.GREEN:
      return { color, backgroundColor: props.theme.colors.brandGreen50 };

    case ButtonColor.RED:
      return { color, backgroundColor: props.theme.colors.error };

    case ButtonColor.WHITE:
      // Switch to blue font for white button
      return {
        color: props.theme.colors.dsBlueDark,
        backgroundColor: props.theme.colors.brandWhite,
      };

    default:
      return {
        color,
        background: props.theme.colors.lspdPrimaryGradient,
        borderColor: props.theme.colors.lspdPrimaryBorder,
      };
  }
}

const StyledBaseButton = styled("button")<IBaseButtonProps>`
  border: ${props => props.theme.lightBorder};
  ${props => (props.borderless ? "border-color: transparent;" : "")}
  border-radius: ${props => props.theme.borderRadius};
  ${props => {
    const { color, backgroundColor, background, borderColor } = getButtonColors(
      props
    );
    return `
      color: ${color};
      background-color: ${backgroundColor};
      background: ${background};
      border: 1px solid ${borderColor}
    `;
  }}
  cursor: ${props => (props.disabled ? "default" : "pointer")};
  font-size: ${props => props.theme.text.size.base};
  line-height: ${props => props.theme.text.lineHeight.medium};
  margin: ${props => props.theme.spacing.xsmall};

  &:disabled {
    opacity: 0.6;
  }

  & > * {
    vertical-align: middle;
  }
`;

export default StyledBaseButton;
